// src/app/store/purchase-order/purchase-order.store.ts

import {computed, inject, Injectable, signal} from '@angular/core';
import {finalize, Observable, of, tap} from "rxjs";
import {DeliveryOrder} from "../../api/models/delivery-order";
import {JsonPatchOperation} from '../../api/models/json-patch-operation';
import {PurchaseOrder} from '../../api/models/purchase-order';
import {DeliveryOrderApiService} from "../../api/services/delivery-order-api.service";
import {BillingDocumentApiService} from "../../api/services";
import {PurchaseOrderApiService} from '../../api/services/purchase-order-api.service';
import {DismissBillingDocument} from "../../api/models/dismiss-billing-document";
import {PurchaseOrderSearch} from "../../api/models/purchase-order-search";
import {map} from "rxjs/operators";
import {PurchaseOrderSearchResult} from "../../api/models/purchase-order-search-result";
import {BillingDocument} from "../../api/models/billing-document";


@Injectable({ providedIn: 'root' })
export class PurchaseOrderStore {
  private poApi = inject(PurchaseOrderApiService);
  private billingDocumentApi = inject(BillingDocumentApiService);
  private deliveryOrderApi = inject(DeliveryOrderApiService);

  private _purchaseOrder = signal<PurchaseOrder | undefined>(undefined);

  // ======== READ-ONLY SIGNALS (replacing selectors) ========
  readonly purchaseOrder = this._purchaseOrder.asReadonly();
  readonly purchaseOrderUuid = computed(() => this._purchaseOrder()?.uuid ?? null);
  readonly billingDocuments = computed(() => this._purchaseOrder()?.billingDocuments ?? []);
  readonly billingDocumentById = (id: number) =>
    this._purchaseOrder()?.billingDocuments?.find(inv => inv.id === id);
  private _searchResults = signal<PurchaseOrder[]>([]);
  readonly searchResults = this._searchResults.asReadonly();
  private _searchResultTotalElements = signal(0);
  readonly searchResultTotalElements = this._searchResultTotalElements.asReadonly();
  private _loading = signal(false);
  readonly loading = this._loading.asReadonly();
  private _accountCodeFilter = signal<string | null>(null);
  readonly accountCodeFilter = this._accountCodeFilter.asReadonly();

  /** Reset a purchase order */
  resetPurchaseOrder() {
    this._purchaseOrder.set(undefined);
  }

  searchOrderNumber(orderNumber: string): Observable<PurchaseOrder[]> {
    this._loading.set(true);

    const body: PurchaseOrderSearch = {
      orderNumber
    };

    return this.poApi.searchPurchaseOrders({
      page: 0,
      size: 10,
      sort: 'orderNumber',
      order: 'asc',
      body
    }).pipe(
      map(res => res.purchaseOrders ?? []),
      tap(res => {
        this._searchResults.set(res);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  searchFull(
    criteria: Partial<PurchaseOrderSearch> = {},
    page: number = 0,
    size: number = 10,
    sort: string = 'creationDate',
    order: 'asc' | 'desc' = 'desc'
  ): Observable<PurchaseOrderSearchResult> {
    this._loading.set(true);

    const searchBody: PurchaseOrderSearch = { ...criteria };

    return this.poApi.searchPurchaseOrders({
      body: searchBody,
      page,
      size,
      sort,
      order
    }).pipe(
      tap(res => {
        this._searchResults.set(res.purchaseOrders ?? []);
        this._searchResultTotalElements.set(res.totalElements ?? 0);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  clearSearchResults() {
    this._searchResults.set([]);
    this._searchResultTotalElements.set(0);
  }

  /** Create a new purchase order */
  createPurchaseOrder(purchaseOrder: PurchaseOrder) {
    this._loading.set(true);
    return this.poApi.createPurchaseOrder({ body: purchaseOrder }).pipe(
      tap((createdPO: PurchaseOrder) => {
        this._purchaseOrder.set(createdPO);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  /** PUT: Replace the whole purchase order */
  updatePurchaseOrder(purchaseOrder: PurchaseOrder) {
    const current = this._purchaseOrder();
    if (!current?.uuid) {
      throw new Error('No purchase order loaded to update.');
    }

    this._loading.set(true);
    return this.poApi.updatePurchaseOrder({ uuid: purchaseOrder.uuid!, body: purchaseOrder }).pipe(
      tap((updatedPO: PurchaseOrder) => {
        this._purchaseOrder.set(updatedPO);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  /** PATCH: Apply JSON Patch operations */
  patchPurchaseOrder(operations: JsonPatchOperation[]) {
    const current = this._purchaseOrder();
    if (!current?.uuid) {
      throw new Error('No purchase order loaded to patch.');
    }

    this._loading.set(true);
    return this.poApi.patchPurchaseOrder({
      uuid: current.uuid,
      body: operations
    }).pipe(
      tap((patchedPO: PurchaseOrder) => {
        this._purchaseOrder.set(patchedPO);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  /** Delete the current purchase order */
  deletePurchaseOrder(uuid: string) {
    this._loading.set(true);
    return this.poApi.deletePurchaseOrder({ uuid }).pipe(
      tap(() => {
        this._purchaseOrder.set(undefined);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  /** Load a purchase order, but avoid duplicate request for same UUID */
  loadPurchaseOrder(uuid: string) {
    if (this._purchaseOrder()?.uuid === uuid) {
      return;
    }

    this._loading.set(true);
    this.poApi.getPurchaseOrder({ uuid })
      .pipe(finalize(() => this._loading.set(false)))
      .subscribe((po) => {
      this._purchaseOrder.set(po);
    });
  }

  loadPurchaseOrderAndReturn(uuid: string, reload: boolean = false): Observable<PurchaseOrder> {
    if (!reload && this._purchaseOrder()?.uuid === uuid) {
      return of(this._purchaseOrder()!);
    }
    this._loading.set(true);
    return this.poApi.getPurchaseOrder({ uuid }).pipe(
      tap(po => this._purchaseOrder.set(po)),
      finalize(() => this._loading.set(false))
    );
  }

  /** Replace current purchase order */
  setPurchaseOrder(purchaseOrder: PurchaseOrder) {
    this._purchaseOrder.set(purchaseOrder);
  }

  /** Update a specific billing document in the purchase order */
  setBillingDocument(updatedBillingDocument: BillingDocument) {
    this._loading.set(true);
    this._purchaseOrder.update((po) => {
      if (!po?.billingDocuments) return po;
      return {
        ...po,
        billingDocuments: po.billingDocuments.map((inv) =>
          inv.id === updatedBillingDocument.id ? updatedBillingDocument : inv
        ),
      };
    });
    this._loading.set(false);
  }

  patchBillingDocument(updatedBillingDocument: BillingDocument) {
    const po = this._purchaseOrder();
    if (!po?.uuid) {
      throw new Error('No purchase order loaded to patch invoice.');
    }

    const index = po.billingDocuments?.findIndex(inv => inv.id === updatedBillingDocument.id);
    if (index === undefined || index < 0) {
      throw new Error(`Billing document with id ${updatedBillingDocument.id} not found in current purchase order.`);
    }

    const patch: JsonPatchOperation[] = [
      {
        op: 'replace',
        path: `/invoices/${index}`,
        value: updatedBillingDocument
      }
    ];

    this._loading.set(true);
    return this.poApi.patchPurchaseOrder({
      uuid: po.uuid,
      body: patch
    }).pipe(
      tap((patchedPO: PurchaseOrder) => {
        this._purchaseOrder.set(patchedPO);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  /** Dismiss a billing document (with optional comment) */
  dismissBillingDocument(billingDocumentId: number, comment?: string) {
    const trimmedComment = comment?.trim();
    const body: DismissBillingDocument = {
      comment: trimmedComment || undefined
    };

    this._loading.set(true);
    return this.billingDocumentApi.dismissBillingDocument({ id: billingDocumentId, body }).pipe(
      tap((updatedBillingDocument: BillingDocument) => {
        if (updatedBillingDocument.purchaseOrderUuid === this._purchaseOrder()?.uuid) {
          this._purchaseOrder.update((po) => {
            if (!po?.billingDocuments) return po;
            return {
              ...po,
              billingDocuments: po.billingDocuments.map(inv =>
                inv.id === billingDocumentId ? updatedBillingDocument : inv
              )
            };
          });
        }
      }),
      finalize(() => this._loading.set(false))
    );
  }

  removeBillingDocumentByInvoiceUuid(invoiceDocumentUuid: string) {
    this._purchaseOrder.update(currentPO => {
      if (!currentPO?.billingDocuments) return currentPO;

      return {
        ...currentPO,
        billingDocuments: currentPO.billingDocuments.filter(
          bd => bd.invoiceDocumentUuid !== invoiceDocumentUuid
        )
      };
    });
  }

  /** Create a new delivery order and attach it to the right billing document */
  createDeliveryOrder(billingDocumentId: number, deliveryOrder: DeliveryOrder) {
    this._loading.set(true);
    return this.deliveryOrderApi.createDeliveryOrder({ body: deliveryOrder }).pipe(
      tap((createdDO: DeliveryOrder) => {
        this._purchaseOrder.update((po) => {
          if (!po?.billingDocuments) return po;
          return {
            ...po,
            billingDocuments: po.billingDocuments.map(inv =>
              inv.id === billingDocumentId ? { ...inv, deliveryOrder: createdDO } : inv
            )
          };
        });
      }),
      finalize(() => this._loading.set(false))
    );
  }

  /** Complete/update an existing delivery order */
  updateDeliveryOrder(billingDocumentId: number, deliveryOrder: DeliveryOrder) {
    if (!deliveryOrder.uuid) {
      throw new Error("Cannot update a delivery order without UUID");
    }

    this._loading.set(true);
    return this.deliveryOrderApi.completeDeliveryOrder({
      uuid: deliveryOrder.uuid,
      body: deliveryOrder
    }).pipe(
      tap((updatedDO: DeliveryOrder) => {
        this._purchaseOrder.update((po) => {
          if (!po?.billingDocuments) return po;
          return {
            ...po,
            billingDocuments: po.billingDocuments.map(inv =>
              inv.id === billingDocumentId ? { ...inv, deliveryOrder: updatedDO } : inv
            )
          };
        });
      }),
      finalize(() => this._loading.set(false))
    );
  }

  setAccountCodeFilter(accountCode: string | null | undefined) {
    if (!accountCode) {  // reset if no account code selected
      this.resetAccountCodeFilter();
      return;
    }

    this._accountCodeFilter.set(accountCode.charAt(0));
  }

  resetAccountCodeFilter() {
    this._accountCodeFilter.set(null);
  }

}
